import { useEffect, useRef, useState, type KeyboardEvent as ReactKeyboardEvent } from "react";
import { Loader2, Search } from "lucide-react";
import { useNavigate } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/components/ui/dialog";
import { SectionHelpTooltip } from "@/components/shared/SectionHelpTooltip";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import { fetchGlobalSearch } from "@/services/global-search.service";
import type { GlobalSearchGroup, GlobalSearchItem } from "@/types/global-search";

const DEBOUNCE_MS = 280;
const MIN_Q = 2;

export type GlobalSearchPaletteProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onPlaceSelect?: (place: { lon: number; lat: number; displayName: string }) => void;
};

function isMacPlatform(): boolean {
  if (typeof navigator === "undefined") return false;
  return /Mac|iPhone|iPad|iPod/i.test(navigator.platform || navigator.userAgent);
}

export function GlobalSearchPalette({
  open,
  onOpenChange,
  onPlaceSelect,
}: GlobalSearchPaletteProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [groups, setGroups] = useState<GlobalSearchGroup[]>([]);
  const [partialErrors, setPartialErrors] = useState<{ type: string; message: string }[]>([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const requestSeq = useRef(0);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const { toast } = useToast();
  const navigate = useNavigate();
  const modKey = isMacPlatform() ? "⌘" : "Ctrl";

  useEffect(() => {
    if (!open) {
      setSearchQuery("");
      setGroups([]);
      setPartialErrors([]);
      setLoading(false);
      setActiveIndex(0);
      return;
    }
    const t = window.setTimeout(() => inputRef.current?.focus(), 30);
    return () => window.clearTimeout(t);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const q = searchQuery.trim();
    if (q.length < MIN_Q) {
      setGroups([]);
      setPartialErrors([]);
      setLoading(false);
      setActiveIndex(0);
      return;
    }

    const seq = ++requestSeq.current;
    setLoading(true);
    const timer = window.setTimeout(() => {
      void (async () => {
        try {
          const res = await fetchGlobalSearch(q);
          if (seq !== requestSeq.current) return;
          setGroups(res.groups ?? []);
          setPartialErrors(res.partial_errors ?? []);
          setActiveIndex(0);
        } catch (err) {
          if (seq !== requestSeq.current) return;
          setGroups([]);
          setPartialErrors([
            {
              type: "request",
              message: err instanceof Error ? err.message : "Ошибка поиска",
            },
          ]);
        } finally {
          if (seq === requestSeq.current) setLoading(false);
        }
      })();
    }, DEBOUNCE_MS);

    return () => window.clearTimeout(timer);
  }, [searchQuery, open]);

  const flatItems = groups.flatMap((g) =>
    g.items.map((item) => ({ group: g, item })),
  );
  const qLen = searchQuery.trim().length;
  const showHint = qLen < MIN_Q;
  const showEmpty = !loading && qLen >= MIN_Q && flatItems.length === 0;

  const selectItem = (item: GlobalSearchItem) => {
    onOpenChange(false);
    if (
      (item.entity_type === "place" || item.entity_type === "gar") &&
      item.lon != null &&
      item.lat != null
    ) {
      const place = {
        lon: item.lon,
        lat: item.lat,
        displayName: item.title,
      };
      if (onPlaceSelect) onPlaceSelect(place);
      else window.dispatchEvent(new CustomEvent("globalSearchPlace", { detail: place }));
      toast({
        title: item.entity_type === "gar" ? "Официальный адрес (ГАР)" : "Адрес",
        description: item.title,
      });
      return;
    }
    if (item.entity_type === "gar") {
      toast({
        title: "Официальный адрес (ГАР)",
        description: "Координаты для этого GUID ещё не связаны — карта не перемещена",
        variant: "destructive",
      });
      return;
    }
    if (item.href) navigate(item.href);
  };

  const onKeyDown = (e: ReactKeyboardEvent) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      if (flatItems.length === 0) return;
      setActiveIndex((i) => (i + 1) % flatItems.length);
      return;
    }
    if (e.key === "ArrowUp") {
      e.preventDefault();
      if (flatItems.length === 0) return;
      setActiveIndex((i) => (i - 1 + flatItems.length) % flatItems.length);
      return;
    }
    if (e.key === "Enter") {
      e.preventDefault();
      const chosen = flatItems[activeIndex]?.item ?? flatItems[0]?.item;
      if (chosen) selectItem(chosen);
    }
  };

  const indexByKey = new Map<string, number>();
  flatItems.forEach(({ group, item }, idx) => {
    indexByKey.set(`${group.type}-${item.id}`, idx);
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={cn(
          "top-[18%] translate-y-0 gap-0 overflow-hidden p-0 sm:max-w-xl",
          "[&>button]:hidden",
        )}
        onOpenAutoFocus={(e) => {
          e.preventDefault();
          inputRef.current?.focus();
        }}
      >
        <DialogTitle className="sr-only">Глобальный поиск</DialogTitle>
        <DialogDescription className="sr-only">
          Поиск по объектам, заявкам, сущностям модулей и адресам. Минимум 2 символа.
        </DialogDescription>

        <div className="flex items-center gap-2 border-b px-3">
          <Search className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
          <input
            ref={inputRef}
            type="search"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="Поиск: объекты, заявки, сущности, адреса…"
            className="h-12 w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground"
            aria-label="Строка глобального поиска"
            autoComplete="off"
            spellCheck={false}
          />
          {loading && <Loader2 className="h-4 w-4 shrink-0 animate-spin text-muted-foreground" />}
        </div>

        <div className="max-h-[min(22rem,50vh)] min-h-[8rem] overflow-y-auto">
          {showHint && (
            <div className="flex h-32 items-center justify-center px-4 text-sm text-muted-foreground">
              Введите запрос длиной от 2 символов
            </div>
          )}
          {showEmpty && (
            <div className="flex h-32 items-center justify-center px-4 text-sm text-muted-foreground">
              Ничего не найдено
            </div>
          )}
          {!showHint &&
            groups.map((group) => {
              if (group.items.length === 0) return null;
              return (
                <div key={group.type} className="border-b border-border/50 last:border-0">
                  <div className="px-3 py-1.5 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                    {group.label}
                  </div>
                  {group.items.map((item) => {
                    const idx = indexByKey.get(`${group.type}-${item.id}`) ?? 0;
                    const active = idx === activeIndex;
                    return (
                      <button
                        key={`${group.type}-${item.id}`}
                        type="button"
                        className={cn(
                          "flex w-full flex-col items-start gap-0.5 px-3 py-2.5 text-left text-sm",
                          active ? "bg-accent text-accent-foreground" : "hover:bg-muted/60",
                        )}
                        onMouseEnter={() => setActiveIndex(idx)}
                        onClick={() => selectItem(item)}
                      >
                        <span className="line-clamp-2 font-medium">{item.title}</span>
                        {item.subtitle ? (
                          <span className="line-clamp-1 text-xs text-muted-foreground">{item.subtitle}</span>
                        ) : null}
                      </button>
                    );
                  })}
                </div>
              );
            })}
          {partialErrors.length > 0 && qLen >= MIN_Q && (
            <div className="px-3 py-2 text-xs text-amber-700 dark:text-amber-400">
              Частичные ошибки: {partialErrors.map((e) => e.type).join(", ")}
            </div>
          )}
        </div>

        <div className="flex items-center justify-between gap-2 border-t bg-muted/30 px-3 py-2 text-[11px] text-muted-foreground">
          <div className="flex min-w-0 items-center gap-1.5">
            <span>Enter — перейти, Esc — закрыть</span>
            <SectionHelpTooltip label="Справка по глобальному поиску" side="top" className="h-5 w-5">
              <p>
                Единый поиск по объектам ОИ, заявкам РД, сущностям ГО и УДГСПЗ, задачам и адресам
                (через настроенный геокодер контура).
              </p>
              <ul className="list-disc space-y-1 pl-4">
                <li>Введите от 2 символов — появится список по категориям.</li>
                <li>Клик по сущности открывает модуль; по адресу — перемещает карту.</li>
                <li>Локальный поиск в панели модуля не заменяется.</li>
                <li>
                  Открыть палитру: иконка лупы в хедере или {modKey}+K.
                </li>
              </ul>
              <p>
                <kbd className="rounded border px-1">Esc</kbd> закрывает палитру;{" "}
                <kbd className="rounded border px-1">Enter</kbd> выбирает активный результат;{" "}
                стрелки вверх/вниз — навигация по списку.
              </p>
            </SectionHelpTooltip>
          </div>
          <kbd className="shrink-0 rounded border bg-background px-1.5 py-0.5 font-mono text-[10px]">
            {modKey}K
          </kbd>
        </div>
      </DialogContent>
    </Dialog>
  );
}
